import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { 
  Search, Filter, Copy, Heart, Star, Zap, MessageSquare, Video, 
  Mail, Users, TrendingUp, BookOpen, Lightbulb, Target, Award,
  Edit3, Save, Plus, ArrowRight, CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MarketingLibrary = () => {
  const { user, token: authToken } = useAuth();
  
  // State management
  const [angles, setAngles] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [customizations, setCustomizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAngle, setSelectedAngle] = useState(null);
  const [customizationMode, setCustomizationMode] = useState(false);
  
  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedSource, setSelectedSource] = useState('all');
  const [showFeaturedOnly, setShowFeaturedOnly] = useState(false);
  const [activeTab, setActiveTab] = useState('angles');
  
  // Customization state
  const [customizationData, setCustomizationData] = useState({
    custom_title: '',
    custom_content: {},
    placeholder_values: {},
    target_industry: '',
    business_type: ''
  });
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchAngles();
    if (activeTab === 'templates') {
      fetchTemplates();
    }
    if (activeTab === 'my-library') {
      fetchCustomizations();
    }
  }, [currentPage, selectedCategory, selectedSource, showFeaturedOnly, searchQuery, activeTab]);

  const fetchAngles = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        page_size: 12,
        ...(searchQuery && { search: searchQuery }),
        ...(selectedCategory !== 'all' && { category: selectedCategory }),
        ...(selectedSource !== 'all' && { source: selectedSource }),
        ...(showFeaturedOnly && { featured: 'true' })
      });

      const response = await fetch(`${BACKEND_URL}/api/marketing/angles?${params}`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      
      const data = await response.json();
      if (data.ok) {
        setAngles(data.angles);
        setTotalPages(Math.ceil(data.total_count / 12));
      }
    } catch (error) {
      console.error('Error fetching marketing angles:', error);
      toast.error("Failed to load marketing angles");
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/marketing/templates`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      
      const data = await response.json();
      if (data.ok) {
        setTemplates(data.templates);
      }
    } catch (error) {
      console.error('Error fetching marketing templates:', error);
      toast.error("Failed to load marketing templates");
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomizations = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/member/marketing/customizations`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      
      const data = await response.json();
      if (data.ok) {
        setCustomizations(data.customizations);
      }
    } catch (error) {
      console.error('Error fetching customizations:', error);
      toast.error("Failed to load your customizations");
    } finally {
      setLoading(false);
    }
  };

  const handleCustomizeAngle = (angle) => {
    setSelectedAngle(angle);
    setCustomizationData({
      custom_title: angle.title,
      custom_content: {
        dm_script: angle.dm_script || '',
        post_script: angle.post_script || '',
        video_script: angle.video_script || '',
        email_script: angle.email_script || ''
      },
      placeholder_values: {},
      target_industry: '',
      business_type: ''
    });
    setCustomizationMode(true);
  };

  const handleSaveCustomization = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/member/marketing/customizations`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          angle_id: selectedAngle.id,
          ...customizationData
        })
      });

      const data = await response.json();
      if (data.ok) {
        toast.success("Customization saved successfully!");
        setCustomizationMode(false);
        setSelectedAngle(null);
        if (activeTab === 'my-library') {
          fetchCustomizations();
        }
      } else {
        toast.error("Failed to save customization");
      }
    } catch (error) {
      console.error('Error saving customization:', error);
      toast.error("Failed to save customization");
    }
  };

  const handleCopyScript = (script, type) => {
    navigator.clipboard.writeText(script);
    toast.success(`${type} script copied to clipboard!`);
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'testimonial': Award,
      'problem_solution': Target,
      'risk_free_trial': CheckCircle,
      'efficiency': TrendingUp,
      'authority_building': Star,
      'manifestation': Lightbulb,
      'storytelling': BookOpen,
      'contrast': Users,
      'what_if': MessageSquare,
      'training': Video
    };
    return icons[category] || MessageSquare;
  };

  const getSourceBadgeColor = (source) => {
    return source === 'conversion_pack' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800';
  };

  const getAccessLevelColor = (level) => {
    const colors = {
      'boot_camp': 'bg-yellow-100 text-yellow-800',
      'rookie': 'bg-green-100 text-green-800', 
      'pro': 'bg-purple-100 text-purple-800',
      'enterprise': 'bg-red-100 text-red-800'
    };
    return colors[level] || 'bg-gray-100 text-gray-800';
  };

  if (customizationMode && selectedAngle) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold">Customize Marketing Angle</h1>
            <p className="text-gray-600">{selectedAngle.title}</p>
          </div>
          <div className="flex space-x-2">
            <Button variant="outline" onClick={() => setCustomizationMode(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveCustomization}>
              <Save className="h-4 w-4 mr-2" />
              Save Customization
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Customization Form */}
          <Card>
            <CardHeader>
              <CardTitle>Customize for Your Business</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="custom_title">Custom Title</Label>
                <Input
                  id="custom_title"
                  value={customizationData.custom_title}
                  onChange={(e) => setCustomizationData(prev => ({
                    ...prev,
                    custom_title: e.target.value
                  }))}
                />
              </div>

              <div>
                <Label htmlFor="target_industry">Target Industry</Label>
                <Select
                  value={customizationData.target_industry}
                  onValueChange={(value) => setCustomizationData(prev => ({
                    ...prev,
                    target_industry: value
                  }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select industry" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="coaching">Coaching</SelectItem>
                    <SelectItem value="consulting">Consulting</SelectItem>
                    <SelectItem value="ecommerce">E-commerce</SelectItem>
                    <SelectItem value="real_estate">Real Estate</SelectItem>
                    <SelectItem value="fitness">Fitness</SelectItem>
                    <SelectItem value="beauty">Beauty</SelectItem>
                    <SelectItem value="technology">Technology</SelectItem>
                    <SelectItem value="finance">Finance</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="business_type">Business Type</Label>
                <Input
                  id="business_type"
                  value={customizationData.business_type}
                  onChange={(e) => setCustomizationData(prev => ({
                    ...prev,
                    business_type: e.target.value
                  }))}
                  placeholder="e.g., Life Coach, Web Designer, etc."
                />
              </div>
            </CardContent>
          </Card>

          {/* Script Customization */}
          <Card>
            <CardHeader>
              <CardTitle>Customize Scripts</CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="dm">
                <TabsList className="grid grid-cols-4 w-full">
                  <TabsTrigger value="dm">DM</TabsTrigger>
                  <TabsTrigger value="post">Post</TabsTrigger>
                  <TabsTrigger value="video">Video</TabsTrigger>
                  <TabsTrigger value="email">Email</TabsTrigger>
                </TabsList>

                <TabsContent value="dm">
                  <div className="space-y-2">
                    <Label>Direct Message Script</Label>
                    <Textarea
                      value={customizationData.custom_content.dm_script}
                      onChange={(e) => setCustomizationData(prev => ({
                        ...prev,
                        custom_content: {
                          ...prev.custom_content,
                          dm_script: e.target.value
                        }
                      }))}
                      rows={4}
                    />
                  </div>
                </TabsContent>

                <TabsContent value="post">
                  <div className="space-y-2">
                    <Label>Social Media Post Script</Label>
                    <Textarea
                      value={customizationData.custom_content.post_script}
                      onChange={(e) => setCustomizationData(prev => ({
                        ...prev,
                        custom_content: {
                          ...prev.custom_content,
                          post_script: e.target.value
                        }
                      }))}
                      rows={4}
                    />
                  </div>
                </TabsContent>

                <TabsContent value="video">
                  <div className="space-y-2">
                    <Label>Video Script</Label>
                    <Textarea
                      value={customizationData.custom_content.video_script}
                      onChange={(e) => setCustomizationData(prev => ({
                        ...prev,
                        custom_content: {
                          ...prev.custom_content,
                          video_script: e.target.value
                        }
                      }))}
                      rows={4}
                    />
                  </div>
                </TabsContent>

                <TabsContent value="email">
                  <div className="space-y-2">
                    <Label>Email Script</Label>
                    <Textarea
                      value={customizationData.custom_content.email_script}
                      onChange={(e) => setCustomizationData(prev => ({
                        ...prev,
                        custom_content: {
                          ...prev.custom_content,
                          email_script: e.target.value
                        }
                      }))}
                      rows={4}
                    />
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Marketing Content Library</h1>
        <p className="text-gray-600">Professional marketing scripts and templates from BBC QuickList experts</p>
      </div>

      {/* Stats Banner */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <Card>
          <CardContent className="p-4 text-center">
            <div className="flex items-center justify-center space-x-2">
              <Lightbulb className="h-5 w-5 text-yellow-500" />
              <span className="text-2xl font-bold text-gray-900">{angles.length}</span>
            </div>
            <p className="text-sm text-gray-600 mt-1">Marketing Angles</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="flex items-center justify-center space-x-2">
              <BookOpen className="h-5 w-5 text-blue-500" />
              <span className="text-2xl font-bold text-gray-900">{templates.length}</span>
            </div>
            <p className="text-sm text-gray-600 mt-1">Templates</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="flex items-center justify-center space-x-2">
              <Edit3 className="h-5 w-5 text-green-500" />
              <span className="text-2xl font-bold text-gray-900">{customizations.length}</span>
            </div>
            <p className="text-sm text-gray-600 mt-1">My Customizations</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="flex items-center justify-center space-x-2">
              <Award className="h-5 w-5 text-purple-500" />
              <span className="text-2xl font-bold text-gray-900">{user?.membership_tier || 'Free'}</span>
            </div>
            <p className="text-sm text-gray-600 mt-1">Access Level</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-3 w-full max-w-md">
          <TabsTrigger value="angles">Marketing Angles</TabsTrigger>
          <TabsTrigger value="templates">Templates</TabsTrigger>
          <TabsTrigger value="my-library">My Library</TabsTrigger>
        </TabsList>

        {/* Marketing Angles Tab */}
        <TabsContent value="angles">
          {/* Filters */}
          <Card className="mb-6">
            <CardContent className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div className="relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search angles..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
                
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    <SelectItem value="testimonial">Testimonials</SelectItem>
                    <SelectItem value="problem_solution">Problem-Solution</SelectItem>
                    <SelectItem value="manifestation">Manifestation</SelectItem>
                    <SelectItem value="storytelling">Storytelling</SelectItem>
                    <SelectItem value="contrast">Contrast</SelectItem>
                    <SelectItem value="what_if">What If</SelectItem>
                    <SelectItem value="training">Training</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={selectedSource} onValueChange={setSelectedSource}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Sources" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Sources</SelectItem>
                    <SelectItem value="conversion_pack">Conversion Pack</SelectItem>
                    <SelectItem value="ad_vault">AdVault</SelectItem>
                  </SelectContent>
                </Select>

                <Button
                  variant={showFeaturedOnly ? "default" : "outline"}
                  onClick={() => setShowFeaturedOnly(!showFeaturedOnly)}
                >
                  <Star className="h-4 w-4 mr-2" />
                  Featured Only
                </Button>

                <Button variant="outline" onClick={fetchAngles}>
                  <Filter className="h-4 w-4 mr-2" />
                  Apply
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Angles Grid */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <Card key={i} className="animate-pulse">
                  <CardContent className="p-6">
                    <div className="h-4 bg-gray-300 rounded w-3/4 mb-2"></div>
                    <div className="h-3 bg-gray-300 rounded w-full mb-4"></div>
                    <div className="h-20 bg-gray-300 rounded"></div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {angles.map((angle) => {
                const CategoryIcon = getCategoryIcon(angle.category);
                return (
                  <Card key={angle.id} className="group hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex items-center space-x-2">
                          <CategoryIcon className="h-5 w-5 text-gray-600" />
                          <div>
                            <CardTitle className="text-lg">{angle.title}</CardTitle>
                            <div className="flex space-x-2 mt-2">
                              <Badge className={getSourceBadgeColor(angle.source)}>
                                {angle.source === 'conversion_pack' ? 'Conversion Pack' : 'AdVault'}
                              </Badge>
                              <Badge className={getAccessLevelColor(angle.access_level)}>
                                {angle.access_level}
                              </Badge>
                              {angle.featured && <Badge className="bg-yellow-100 text-yellow-800">Featured</Badge>}
                            </div>
                          </div>
                        </div>
                        <div className="text-right text-sm text-gray-500">
                          <div>Score: {angle.effectiveness_score}/10</div>
                          <div>{angle.usage_count} uses</div>
                        </div>
                      </div>
                    </CardHeader>
                    
                    <CardContent className="space-y-4">
                      {/* Script Previews */}
                      <Tabs defaultValue="dm" className="w-full">
                        <TabsList className="grid w-full grid-cols-4">
                          <TabsTrigger value="dm">DM</TabsTrigger>
                          <TabsTrigger value="post">Post</TabsTrigger>
                          <TabsTrigger value="video">Video</TabsTrigger>
                          <TabsTrigger value="email">Email</TabsTrigger>
                        </TabsList>
                        
                        <TabsContent value="dm" className="space-y-2">
                          <div className="bg-gray-50 p-3 rounded text-sm">
                            {angle.dm_script || 'No DM script available'}
                          </div>
                          {angle.dm_script && (
                            <Button 
                              size="sm" 
                              variant="outline" 
                              onClick={() => handleCopyScript(angle.dm_script, 'DM')}
                            >
                              <Copy className="h-4 w-4 mr-2" />
                              Copy DM
                            </Button>
                          )}
                        </TabsContent>
                        
                        <TabsContent value="post" className="space-y-2">
                          <div className="bg-gray-50 p-3 rounded text-sm">
                            {angle.post_script || 'No post script available'}
                          </div>
                          {angle.post_script && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleCopyScript(angle.post_script, 'Post')}
                            >
                              <Copy className="h-4 w-4 mr-2" />
                              Copy Post
                            </Button>
                          )}
                        </TabsContent>
                        
                        <TabsContent value="video" className="space-y-2">
                          <div className="bg-gray-50 p-3 rounded text-sm">
                            {angle.video_script || 'No video script available'}
                          </div>
                          {angle.video_script && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleCopyScript(angle.video_script, 'Video')}
                            >
                              <Copy className="h-4 w-4 mr-2" />
                              Copy Video
                            </Button>
                          )}
                        </TabsContent>
                        
                        <TabsContent value="email" className="space-y-2">
                          <div className="bg-gray-50 p-3 rounded text-sm">
                            {angle.email_script || 'No email script available'}
                          </div>
                          {angle.email_script && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleCopyScript(angle.email_script, 'Email')}
                            >
                              <Copy className="h-4 w-4 mr-2" />
                              Copy Email
                            </Button>
                          )}
                        </TabsContent>
                      </Tabs>

                      {/* Actions */}
                      <div className="flex justify-between">
                        <div className="flex space-x-2">
                          <Badge variant="outline">{angle.tone}</Badge>
                          <Badge variant="outline">{angle.category}</Badge>
                        </div>
                        <Button onClick={() => handleCustomizeAngle(angle)}>
                          <Edit3 className="h-4 w-4 mr-2" />
                          Customize
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates">
          <div className="text-center py-12">
            <BookOpen className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">Marketing Templates Coming Soon</h3>
            <p className="text-gray-600 mb-6">Pre-built combinations of marketing angles for specific industries and use cases</p>
            <Button disabled>
              <Plus className="h-4 w-4 mr-2" />
              Browse Templates
            </Button>
          </div>
        </TabsContent>

        {/* My Library Tab */}
        <TabsContent value="my-library">
          {customizations.length === 0 ? (
            <div className="text-center py-12">
              <Edit3 className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">No Customizations Yet</h3>
              <p className="text-gray-600 mb-6">Start customizing marketing angles to build your personal library</p>
              <Button onClick={() => setActiveTab('angles')}>
                <ArrowRight className="h-4 w-4 mr-2" />
                Browse Marketing Angles
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {customizations.map((customization) => (
                <Card key={customization.id} className="group hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-lg">{customization.custom_title || 'Untitled'}</CardTitle>
                        <CardDescription>
                          {customization.target_industry} • {customization.business_type}
                        </CardDescription>
                      </div>
                      {customization.is_favorite && (
                        <Heart className="h-5 w-5 text-red-500 fill-current" />
                      )}
                    </div>
                  </CardHeader>
                  
                  <CardContent>
                    <div className="space-y-2 mb-4">
                      <Badge variant="outline">{customization.status}</Badge>
                      <div className="text-sm text-gray-600">
                        Used {customization.usage_count} times
                      </div>
                    </div>
                    
                    <div className="flex justify-end space-x-2">
                      <Button size="sm" variant="outline">
                        <Edit3 className="h-4 w-4 mr-2" />
                        Edit
                      </Button>
                      <Button size="sm">
                        <Copy className="h-4 w-4 mr-2" />
                        Use
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Pagination */}
      {totalPages > 1 && activeTab === 'angles' && (
        <div className="flex justify-center mt-8 space-x-2">
          <Button 
            variant="outline" 
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(currentPage - 1)}
          >
            Previous
          </Button>
          
          {[...Array(totalPages)].map((_, index) => (
            <Button
              key={index}
              variant={currentPage === index + 1 ? "default" : "outline"}
              onClick={() => setCurrentPage(index + 1)}
            >
              {index + 1}
            </Button>
          ))}
          
          <Button 
            variant="outline" 
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(currentPage + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
};

export default MarketingLibrary;